// audio.js - TapTap audio engine
//
// Bilateral stimulation is the point of the app: each successive tap should
// alternate between the left and right ear. We use the Web Audio API so we can
// pan precisely and synthesise tones without shipping large sound files.
//
// The AudioContext is created lazily on the first tap. Mobile browsers (and the
// Capacitor WebView) block audio until a user gesture, so we must not create it
// at import time.

let audioCtx = null;

const getCtx = () => {
  if (!audioCtx || audioCtx.state === 'closed') {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  // iOS leaves the context 'suspended' until resumed inside a gesture, and
  // 'interrupted' after a system sheet (Apple purchase, Face ID) or an app
  // switch. Resuming only 'suspended' left sound off until a relaunch.
  if (audioCtx.state !== 'running') {
    audioCtx.resume?.()?.catch?.(() => {});
  }
  return audioCtx;
};

// A purchase sheet or app switch can leave iOS Web Audio interrupted. Sources
// started before resume completes may be dropped, so schedule tap feedback only
// after the context is actually running again.
const playWhenRunning = (ctx, play) => {
  if (ctx.state === 'running') { play(); return; }
  if (!ctx.resume) return;
  ctx.resume().then(() => {
    if (ctx.state === 'running') play();
  }).catch(() => {});
};

// Returning to the app also ends an interruption; wake the context then too,
// rather than waiting for the next tap.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && audioCtx && audioCtx.state !== 'running' && audioCtx.state !== 'closed') {
      audioCtx.resume?.()?.catch?.(() => {});
    }
  });
}

// Clamp a 0-100 volume slider value to a 0-1 gain, kept gentle for a calming app.
const toGain = (volume) => {
  const v = typeof volume === 'number' ? volume : 50;
  return Math.max(0, Math.min(1, v / 100)) * 0.4;
};

// Play a single panned tone. `pan` is -1 (left) .. 1 (right).
const playTone = (ctx, { freq, pan, gain, duration = 0.18, type = 'sine' }) => {
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;

  const amp = ctx.createGain();
  // Quick attack, smooth exponential release so taps feel soft, not clicky.
  amp.gain.setValueAtTime(0.0001, now);
  amp.gain.exponentialRampToValueAtTime(gain, now + 0.012);
  amp.gain.exponentialRampToValueAtTime(0.0001, now + duration);

  // StereoPannerNode isn't on every WebView; fall back to a channel split.
  let panner;
  if (ctx.createStereoPanner) {
    panner = ctx.createStereoPanner();
    panner.pan.value = pan;
    osc.connect(amp).connect(panner).connect(ctx.destination);
  } else {
    panner = ctx.createPanner();
    panner.panningModel = 'equalpower';
    panner.setPosition(pan, 0, 1 - Math.abs(pan));
    osc.connect(amp).connect(panner).connect(ctx.destination);
  }

  osc.start(now);
  osc.stop(now + duration + 0.02);
};

// A soft click: a very short, low-pass-filtered burst of decaying noise. No
// sustained pitch, so it reads as a gentle tick/click rather than a tone.
const playSoftClick = (ctx, { pan, gain }) => {
  const now = ctx.currentTime;
  const dur = 0.045; // short — a click, not a note

  // Build a tiny noise buffer that fades out across its length.
  const length = Math.max(1, Math.ceil(ctx.sampleRate * dur));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    const t = i / length;
    data[i] = (Math.random() * 2 - 1) * (1 - t) * (1 - t);
  }

  const src = ctx.createBufferSource();
  src.buffer = buffer;

  // Low-pass keeps it soft (no sharp/harsh top end).
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2200;
  lp.Q.value = 0.8;

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0.0001, now);
  amp.gain.exponentialRampToValueAtTime(Math.max(gain, 0.0001), now + 0.003);
  amp.gain.exponentialRampToValueAtTime(0.0001, now + dur);

  if (ctx.createStereoPanner) {
    const panner = ctx.createStereoPanner();
    panner.pan.value = pan;
    src.connect(lp).connect(amp).connect(panner).connect(ctx.destination);
  } else {
    const panner = ctx.createPanner();
    panner.panningModel = 'equalpower';
    panner.setPosition(pan, 0, 1 - Math.abs(pan));
    src.connect(lp).connect(amp).connect(panner).connect(ctx.destination);
  }

  src.start(now);
  src.stop(now + dur + 0.02);
};

// Normal mode: a soft bilateral click. `side` (-1 left / 1 right) is owned by the
// caller so the audio pan stays in lock-step with the on-screen movement.
export const playAudioFeedback = (enabled, volume, side = -1) => {
  if (enabled === false) return;
  const ctx = getCtx();
  if (!ctx) return;

  // A noise burst reads quieter than a tone, so lift the click 25% above the
  // standard gain for comparable presence.
  playWhenRunning(ctx, () => playSoftClick(ctx, { pan: side, gain: toGain(volume) * 1.25 }));
};

// Resume/create the AudioContext from within a user gesture (e.g. starting a
// timer) so a later, non-gesture sound — the session chime — can still play.
export const unlockAudio = () => { getCtx(); };

// A gentle three-note ascending chime to mark the end of a timed session.
export const playSessionEndChime = (volume = 60) => {
  const ctx = getCtx();
  if (!ctx) return;
  const peak = toGain(volume);
  [523.25, 659.25, 783.99].forEach((freq, i) => { // C5, E5, G5
    const start = ctx.currentTime + i * 0.18;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(0.0001, start);
    amp.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0001), start + 0.02);
    amp.gain.exponentialRampToValueAtTime(0.0001, start + 0.9);
    osc.connect(amp).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + 0.95);
  });
};

// Fallback synth tone per animal, used only until/unless a real recording is
// present. Distinct enough to tell the animals apart on tone alone.
const ANIMAL_TONES = {
  cat: { freq: 660, type: 'triangle' },
  dog: { freq: 220, type: 'sawtooth' },
  cow: { freq: 130, type: 'sawtooth' },
  sheep: { freq: 350, type: 'triangle' },
  pig: { freq: 300, type: 'square' },
  duck: { freq: 500, type: 'square' },
  lion: { freq: 110, type: 'sawtooth' },
  elephant: { freq: 90, type: 'sawtooth' },
  frog: { freq: 240, type: 'square' },
  horse: { freq: 200, type: 'triangle' },
  chicken: { freq: 520, type: 'square' },
  snake: { freq: 180, type: 'sawtooth' },
  owl: { freq: 280, type: 'sine' },
  monkey: { freq: 700, type: 'square' },
};

// Real recordings live at public/sounds/<key>.mp3. We decode each one once and
// cache the AudioBuffer. Cache values: undefined = not tried, Promise = loading,
// AudioBuffer = ready, null = unavailable (missing/failed → use the synth).
const bufferCache = new Map();

// WAV (LPCM), not AAC/m4a: iOS Web Audio decodeAudioData reliably decodes WAV on
// real devices, whereas afconvert-produced .m4a decoded only in the Simulator
// (which borrows macOS codecs) and fell back to the synth on a physical iPhone.
const soundUrl = (key) => `${process.env.PUBLIC_URL || ''}/sounds/${key}.wav`;

// Load the file as an ArrayBuffer via XHR. We use XHR rather than fetch because
// on a physical iPhone the bundled assets are served from the capacitor://
// custom scheme, where a fetch() can resolve with status 0 (treated as "not ok")
// and fall back to the synth. XHR handles the local/custom-scheme case (status 0
// = success) reliably, which fetch did not on-device.
const fetchArrayBuffer = (url) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.responseType = 'arraybuffer';
  xhr.onload = () => {
    // 0 = success for non-http(s) schemes like capacitor://; 2xx for http.
    if ((xhr.status === 0 || (xhr.status >= 200 && xhr.status < 300)) && xhr.response) {
      resolve(xhr.response);
    } else {
      reject(new Error(`status ${xhr.status}`));
    }
  };
  xhr.onerror = () => reject(new Error('request failed'));
  xhr.send();
});

const loadAnimalBuffer = (ctx, key) => {
  if (bufferCache.has(key)) return bufferCache.get(key);

  const promise = fetchArrayBuffer(soundUrl(key))
    // decodeAudioData is callback-style on older WebKit; wrap to be safe.
    .then((data) => new Promise((resolve, reject) => {
      ctx.decodeAudioData(data, resolve, reject);
    }))
    .then((buffer) => {
      bufferCache.set(key, buffer);
      return buffer;
    })
    .catch(() => {
      bufferCache.set(key, null); // give up on this animal; synth from now on
      return null;
    });

  bufferCache.set(key, promise);
  return promise;
};

// Play a decoded recording, panned to the bilateral `side`.
const playBuffer = (ctx, buffer, { pan, gain }) => {
  const now = ctx.currentTime;
  const src = ctx.createBufferSource();
  src.buffer = buffer;

  const amp = ctx.createGain();
  amp.gain.value = gain;

  if (ctx.createStereoPanner) {
    const panner = ctx.createStereoPanner();
    panner.pan.value = pan;
    src.connect(amp).connect(panner).connect(ctx.destination);
  } else {
    const panner = ctx.createPanner();
    panner.panningModel = 'equalpower';
    panner.setPosition(pan, 0, 1 - Math.abs(pan));
    src.connect(amp).connect(panner).connect(ctx.destination);
  }

  src.start(now);
};

const playAnimalSynth = (ctx, animalKey, volume, side) => {
  const tone = ANIMAL_TONES[animalKey] || ANIMAL_TONES.cat;
  playTone(ctx, {
    freq: tone.freq,
    type: tone.type,
    pan: side,
    gain: toGain(volume),
    duration: 0.32, // a touch longer so it reads as an animal "call"
  });
};

// Recordings sit at full level; keep them a touch below unity for comfort.
const toSampleGain = (volume) => {
  const v = typeof volume === 'number' ? volume : 50;
  return Math.max(0, Math.min(1, v / 100)) * 0.9;
};

// Per-animal level trim for clips that are naturally quiet/loud. 1 = unchanged.
const ANIMAL_GAIN = {
  snake: 2.4, // a hiss is low-energy; lift it to match the others
};
const animalGain = (key) => ANIMAL_GAIN[key] || 1;

// Warm the cache so the first tap on a freshly selected animal plays the real
// recording rather than the synth fallback. Safe to call without a gesture.
export const preloadAnimalSound = (animalKey) => {
  const ctx = getCtx();
  if (ctx && animalKey) loadAnimalBuffer(ctx, animalKey);
};

// Kids mode: play the animal's sound, panned to the current bilateral `side`.
// Prefer the real recording; fall back to the synth if it isn't ready/available.
export const playAnimalSound = (animalKey, enabled, volume, side = -1) => {
  if (enabled === false) return;
  const ctx = getCtx();
  if (!ctx) return;

  loadAnimalBuffer(ctx, animalKey);
  playWhenRunning(ctx, () => {
    const cached = loadAnimalBuffer(ctx, animalKey);
    if (cached && typeof cached.then !== 'function') {
      // Already decoded → play the recording.
      playBuffer(ctx, cached, { pan: side, gain: toSampleGain(volume) * animalGain(animalKey) });
    } else {
      // Missing or still loading → give immediate audible feedback.
      playAnimalSynth(ctx, animalKey, volume, side);
    }
  });
};
